import { gql, useMutation } from "@apollo/client";
import { FormikTextField } from "components/atoms/formik";
import { CloudDrawer, CloudDrawerProps } from "components/modules/CloudDrawer";
import { useFormik } from "formik";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import { QUERY_ROBOT_BASIC_INFO } from "../robotWidgets/RobotBasicInfo";

const UPDATE_ROBOT_NICKNAME = gql(`
  mutation UpdateRobotNickname($robotId: String!, $nickname: String) {
    updateRobot(id: $robotId, nickname: $nickname) {
      id
      active {
        nickname
      }
    }
  }
`);

interface Props {
  open: CloudDrawerProps["open"];
  onClose: CloudDrawerProps["onClose"];
  robot: any;
}

type Values = {
  robotNickname?: string;
};

export const EditRobotNickname = ({ open, onClose, robot }: Props) => {
  const intl = useIntl();
  const { robotId } = useParams();

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const [updateRobotNickname] = useMutation(UPDATE_ROBOT_NICKNAME, {
    update(cache: any) {
      cache.modify({
        fields: {
          globalSearch(existing: any, { INVALIDATE }: any) {
            return INVALIDATE;
          },
          robots(existing: any, { INVALIDATE }: any) {
            return INVALIDATE;
          },
          robot(existing: any, { INVALIDATE }: any) {
            return INVALIDATE;
          },
        },
      });
    },
    refetchQueries: [
      {
        query: QUERY_ROBOT_BASIC_INFO,
        variables: { robotId: robotId ?? "" },
      },
    ],
    awaitRefetchQueries: true,
  });

  const formik = useFormik<Values>({
    initialValues: {
      robotNickname: "",
    },
    validationSchema: Yup.object({
      robotNickname: Yup.string().required(
        intl.formatMessage({
          id: "required",
          defaultMessage: "Required",
        }),
      ),
    }),
    onSubmit(values) {
      const snackbarId = enqueueSnackbar("Updating robot nickname...", {
        variant: "loading",
        persist: true,
      });
      updateRobotNickname({
        variables: {
          robotId: robotId ?? robot?.id ?? "",
          nickname: values.robotNickname ?? "",
        },
        onCompleted: () => {
          enqueueSnackbar(`Updated robot nickname`, {
            variant: "success",
          });
          closeSnackbar(snackbarId);
          handleClose();
        },
        onError: (e) => {
          console.error(e);
          enqueueSnackbar(`Could not update robot nickname`, {
            variant: "error",
          });
          closeSnackbar(snackbarId);
        },
      });
    },
  });

  useEffect(() => {
    (() => formik.validateForm())();
  }, []);

  return (
    <form>
      <CloudDrawer
        open={open}
        onClose={handleClose}
        header={`Robot ${robot?.serialNumber}: Edit nickname`}
        action={{
          label: "Save changes",
          onClick: formik.handleSubmit,
          disabled:
            formik.isValid === false ||
            formik.values.robotNickname === robot?.active?.nickname,
        }}
        secondaryAction={{
          label: "Remove nickname",
          onClick: () => {
            const snackbarId = enqueueSnackbar("Removing robot nickname...", {
              variant: "loading",
              persist: true,
            });
            updateRobotNickname({
              variables: {
                robotId: robotId ?? robot?.id ?? "",
                nickname: null,
              },
              onCompleted: () => {
                enqueueSnackbar(`Removed robot nickname`, {
                  variant: "success",
                });
                closeSnackbar(snackbarId);
                handleClose();
              },
              onError: (e) => {
                console.error(e);
                enqueueSnackbar(`Could not remove robot nickname`, {
                  variant: "error",
                });
                closeSnackbar(snackbarId);
              },
            });
          },
          disabled: !robot?.active?.nickname,
        }}
      >
        <FormikTextField
          name="robotNickname"
          formik={formik}
          label="Robot nickname"
          placeholder={robot?.active?.nickname}
        />
      </CloudDrawer>
    </form>
  );
};
